import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Box, Building2, Lock, Phone, Pencil } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';


import { ClientContactsTab } from './client-detail/ClientContactsTab';
import { ClientCredentialsTab } from './client-detail/ClientCredentialsTab';
import { ClientFormDialog } from './client-detail/ClientFormDialog';
import { ClientGeneralTab } from './client-detail/ClientGeneralTab';
import { ClientServicesTab } from './client-detail/ClientServicesTab';

import { useAuth } from '@/contexts/AuthContext';
import { apiFetch } from '@/lib/api';

export interface ClientDetail {
  id: string;
  codigoCliente: string | null;
  cpfCnpj: string;
  razaoSocial: string;
  nomeFantasia: string | null;
  inscricaoMunicipal: string | null;
  email: string | null;
  telefone: string | null;
  enderecoLogradouro: string | null;
  enderecoNumero: string | null;
  enderecoComplemento: string | null;
  enderecoBairro: string | null;
  enderecoCidade: string;
  enderecoUf: string;
  enderecoCep: string | null;
}

type TabKey = 'geral' | 'contatos' | 'servicos' | 'cofre';

export function ClientDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [params, setParams] = useSearchParams();
  const { user } = useAuth();
  const isAdmin = user?.role === 'SUPER_ADMIN' || user?.role === 'ADMIN';
  const initialTab = (params.get('tab') as TabKey) || 'geral';
  const [tab, setTab] = useState<TabKey>(initialTab);
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    const next = new URLSearchParams(params);
    next.set('tab', tab);
    setParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const client = useQuery({
    queryKey: ['client', id],
    queryFn: () => apiFetch<ClientDetail>(`/clients/${id}`),
    enabled: !!id,
  });

  if (!id) return null;

  return (
    <div className="space-y-6">
      <header className="space-y-2">
        <Link
          to="/clients"
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-3 w-3" /> Voltar para clientes
        </Link>
        <div className="flex items-end justify-between gap-4">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight">
              {client.data?.razaoSocial ?? 'Carregando…'}
            </h1>
            {client.data && (
              <p className="text-sm text-muted-foreground font-mono">
                {client.data.cpfCnpj} • {client.data.enderecoCidade}/{client.data.enderecoUf}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="flex items-center gap-1 rounded-md border border-input bg-background px-3 py-2 text-sm hover:bg-accent"
          >
            <Pencil className="h-4 w-4" /> Editar
          </button>
        </div>
      </header>

      {/* Tabs */}
      <div className="flex border-b border-border">
        <TabButton active={tab === 'geral'} onClick={() => setTab('geral')} icon={Building2}>
          Geral
        </TabButton>
        <TabButton active={tab === 'contatos'} onClick={() => setTab('contatos')} icon={Phone}>
          Contatos
        </TabButton>
        <TabButton active={tab === 'servicos'} onClick={() => setTab('servicos')} icon={Box}>
          Serviços
        </TabButton>
        {isAdmin && (
          <TabButton active={tab === 'cofre'} onClick={() => setTab('cofre')} icon={Lock}>
            Cofre
          </TabButton>
        )}
      </div>

      {/* Tab content */}
      <div>
        {tab === 'geral' && client.data && <ClientGeneralTab client={client.data} />}
        {tab === 'contatos' && <ClientContactsTab clientId={id} />}
        {tab === 'servicos' && <ClientServicesTab clientId={id} />}
        {tab === 'cofre' && isAdmin && <ClientCredentialsTab clientId={id} />}
      </div>

      {editing && client.data && (
        <ClientFormDialog
          initial={client.data}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            client.refetch();
          }}
        />
      )}
    </div>
  );
}

function TabButton({
  active,
  onClick,
  icon: Icon,
  children,
}: {
  active: boolean;
  onClick: () => void;
  icon: typeof Building2;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center gap-2 border-b-2 px-4 py-2 text-sm transition-colors ${
        active
          ? 'border-primary text-primary font-medium'
          : 'border-transparent text-muted-foreground hover:text-foreground'
      }`}
    >
      <Icon className="h-4 w-4" />
      {children}
    </button>
  );
}
